import React, { useState, useEffect } from 'react';
import { sinistroService } from '../../services';
import { formatDate, formatCpfCnpj, formatCurrency } from '../../utils/formatters';
import SinistroModal from './SinistroModal';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface SinistroTableProps {
  onEdit?: (sinistro: any) => void;
  onView?: (sinistro: any) => void;
  refreshKey?: number;
  onDeleted?: () => void;
}

export const SinistroTable: React.FC<SinistroTableProps> = ({
  onEdit,
  onView,
  refreshKey,
  onDeleted
}) => {
  const notifier = useNotifier();
  const [sinistros, setSinistros] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // filters UI hidden by request; keep `selectedFlags` state for programmatic use
  const [sortField, setSortField] = useState<string>('data');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Selected flags for filtering (supports multiple flags)
  const [selectedFlags, setSelectedFlags] = useState<string[]>([]);

  // Modal states
  const [showSinistroModal, setShowSinistroModal] = useState(false);
  const [selectedSinistro, setSelectedSinistro] = useState<any>(null);
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sinistroToDelete, setSinistroToDelete] = useState<any>(null);

  const loadSinistros = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Debug: verificar autenticação
      const token = localStorage.getItem('authToken');
      const user = localStorage.getItem('user');
      console.log('Token presente:', !!token);
      console.log('User presente:', !!user);
      console.log('Carregando sinistros...', { currentPage, itemsPerPage, searchTerm, sortField, sortOrder });
      
      const queryParams: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortField,
        sortOrder
      };

      // include empresaId if set in localStorage (optional filter)
      const empresaId = localStorage.getItem('empresaId');
      if (empresaId) queryParams.empresaId = empresaId;

      // If search term present, always use search param (flags take precedence)
      if (searchTerm.trim()) {
        queryParams.search = searchTerm.trim();
      }

      let response: any;
      // If flags selected, use the flag endpoint which accepts comma-separated flags
      if (selectedFlags && selectedFlags.length > 0) {
        const flagsPath = selectedFlags.join(',');
        response = await sinistroService.getByFlags(flagsPath, queryParams);
      } else if (queryParams.search) {
        // when a search term is present, use the dedicated search endpoint
        response = await sinistroService.search(queryParams);
      } else {
        // fallback to list (supports sort, page, limit)
        response = await sinistroService.list(queryParams);
      }
      
      console.log('Resposta de sinistros:', response);
      const data = response?.data || response || [];
      if (Array.isArray(data)) {
        setSinistros(data);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
      } else if (data.items && Array.isArray(data.items)) {
        setSinistros(data.items);
        setTotalPages(data.totalPages || 1);
      } else {
        setSinistros([]);
        setTotalPages(1);
      }
    } catch (err: any) {
      console.error('Erro ao carregar sinistros:', err);
      console.error('Status do erro:', err.status);
      console.error('Mensagem do erro:', err.message);
      setError(err.message || 'Erro ao carregar sinistros');
      setSinistros([]);
      
      // Se for erro 401, não mostrar erro na tela pois o usuário já foi redirecionado
      if (err.status === 401) {
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSinistros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, sortField, sortOrder, refreshKey, selectedFlags]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Função desabilitada temporariamente até endpoint de flags estar funcionando
  // const handleFlagFilterChange = (flag: keyof typeof flagFilters) => {
  //   setFlagFilters(prev => ({
  //     ...prev,
  //     [flag]: !prev[flag]
  //   }));
  //   setCurrentPage(1); // Reset to first page when filters change
  // };

  const handleViewSinistro = (sinistro: any) => {
    setSelectedSinistro(sinistro);
    setModalReadOnly(true);
    setShowSinistroModal(true);
  };

  const handleEditSinistro = (sinistro: any) => {
    setSelectedSinistro(sinistro);
    setModalReadOnly(false);
    setShowSinistroModal(true);
  };

  const handleCloseModal = () => {
    setShowSinistroModal(false);
    setSelectedSinistro(null);
    setModalReadOnly(false);
  };

  const handleSaveSinistro = () => {
    loadSinistros(); // Reload table after save
  };

  // Recalcular totalPages quando sinistros mudar
  useEffect(() => {
    setTotalPages(Math.ceil(sinistros.length / itemsPerPage) || 1);
    if (currentPage > Math.ceil(sinistros.length / itemsPerPage) && sinistros.length > 0) {
      setCurrentPage(1);
    }
  }, [sinistros.length, currentPage, itemsPerPage]);

  const handleDelete = (sinistro: any) => {
    if (!sinistro.id) return;
    setSinistroToDelete(sinistro);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!sinistroToDelete) return;

    try {
      const response = await sinistroService.remove(sinistroToDelete.id);
      console.log('Resposta da exclusão:', response);
      notifier.show({ message: 'Sinistro excluído com sucesso!', severity: 'success' });
      await loadSinistros();
      onDeleted?.();
      setShowDeleteConfirm(false);
      setSinistroToDelete(null);
    } catch (err: any) {
      console.error('Erro ao excluir sinistro:', err);
      notifier.show({ message: err.message || 'Erro ao excluir sinistro', severity: 'error' });
    }
  };

  return (
    <div className="space-y-4">
      {/* Header e Busca */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>
          Sinistros ({sinistros.length})
        </h2>

        <div className="flex gap-1 flex-wrap">
          {/* Campo de ordenação */}
          <div className="flex items-center gap-1">
            <label className="text-xs" style={{ color: '#3d2b56' }}>Ordenar por:</label>
            <select
              value={sortField}
              onChange={(e) => {
                setSortField(e.target.value);
                setCurrentPage(1);
              }}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '140px'
              }}
            >
              <option value="data">Data</option>
              <option value="numeroDocumento">Documento</option>
              <option value="numero_sinistro">Nº Sinistro</option>
              <option value="apolice">Apólice</option>
              <option value="endosso">Endosso</option>
              <option value="cpf_cnpj">CPF/CNPJ</option>
              <option value="cliente.nome">Cliente (nome)</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          
          <form onSubmit={(e) => { e.preventDefault(); setCurrentPage(1); loadSinistros(); }} className="flex gap-1">
            <input
              type="text"
              placeholder="Buscar sinistros..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
              style={{ 
                borderColor: '#C3B4DE',
                backgroundColor: '#FFFFFF',
                color: '#121212',
                minWidth: '160px'
              }}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-2 py-1 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-1 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: '#7A61B2' }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </form>

          {/* filtros UI hidden */}
        </div>
      </div>

      {/* filters UI removed */}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="text-sm" style={{ color: '#7A61B2' }}>Carregando...</div>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="bg-red-50 rounded-lg shadow p-6 text-center">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && !error && sinistros.length > 0 && (
        <div className="space-y-3">
          {sinistros
            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            .map((sinistro: any) => {
            // Determinar cor da borda baseada no status de conclusão
            let borderColor = '#F59E0B'; // Amarelo padrão para em andamento
            if (sinistro.concluido) {
              borderColor = '#10B981'; // Verde para concluído
            } else if (sinistro.pagamento) {
              borderColor = '#06B6D4'; // Cyan para em pagamento
            } else if (sinistro.aprovacao) {
              borderColor = '#8B5CF6'; // Roxo para em aprovação
            }
            
            return (
              <div 
                key={sinistro.id}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow relative overflow-hidden"
                style={{ borderLeft: `4px solid ${borderColor}` }}
              >
                <div className="p-4">
                  {/* Header com Status e Ações */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                        sinistro.concluido ? 'bg-green-50 text-green-700' :
                        sinistro.pagamento ? 'bg-cyan-50 text-cyan-700' :
                        sinistro.aprovacao ? 'bg-purple-50 text-purple-700' :
                        'bg-yellow-50 text-yellow-700'
                      }`}>
                        {sinistro.concluido ? '✓ Concluído' :
                         sinistro.pagamento ? '💰 Pagamento' :
                         sinistro.aprovacao ? '✓ Aprovação' :
                         '⏱ Em Andamento'}
                      </span>
                      <h3 className="text-sm font-semibold" style={{ color: '#3d2b56' }}>
                        Sinistro {sinistro.numero_sinistro || '-'}
                      </h3>
                    </div>
                    
                    {/* Botões de Ação */}
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={(e) => { e.stopPropagation(); onView ? onView(sinistro) : handleViewSinistro(sinistro); }} 
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors" 
                        title="Ver Detalhes"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); onEdit ? onEdit(sinistro) : handleEditSinistro(sinistro); }} 
                        className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                        title="Editar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDelete(sinistro); }} 
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors" 
                        title="Excluir"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Grid de Informações */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                    {/* Cliente - SEMPRE PRIMEIRO */}
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Cliente</div>
                        <div className="font-semibold text-gray-900 truncate">{sinistro.clientes?.[0]?.nome || sinistro.cliente?.nome || sinistro.nome || 'N/A'}</div>
                        { (sinistro.cpf_cnpj || sinistro.cliente?.cpfCnpj) && <div className="text-gray-500 text-xs">{formatCpfCnpj(sinistro.cpf_cnpj || sinistro.cliente?.cpfCnpj)}</div>}
                      </div>
                    </div>

                    {/* Documento */}
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Documento</div>
                        <div className="font-semibold text-gray-900 truncate">{sinistro.numeroDocumento || '-'}</div>
                      </div>
                    </div>

                    {/* Data */}
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Data</div>
                        <div className="font-medium text-gray-900">{sinistro.data ? formatDate(sinistro.data) : '-'}</div>
                      </div>
                    </div>

                    {/* Tipo */}
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Tipo</div>
                        <div className="font-medium text-gray-900 truncate">{sinistro.tipo || '-'}</div>
                      </div>
                    </div>

                    {/* Valor Franquia */}
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Valor Franquia</div>
                        <div className="font-medium text-gray-900">{sinistro.valor_franquia ? formatCurrency(Number(sinistro.valor_franquia)) : '-'}</div>
                      </div>
                    </div>

                    {/* Apólice */}
                    {sinistro.apolice && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Apólice</div>
                          <div className="font-medium text-gray-900 truncate">{sinistro.apolice}</div>
                        </div>
                      </div>
                    )}

                    {/* Endosso */}
                    {sinistro.endosso && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Endosso</div>
                          <div className="font-medium text-gray-900 truncate">{sinistro.endosso}</div>
                        </div>
                      </div>
                    )}

                    {/* Telefone */}
                    {(sinistro.clientes?.[0]?.telefone || sinistro.cliente?.telefone) && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Telefone</div>
                          <div className="font-medium text-gray-900">{sinistro.clientes?.[0]?.telefone || sinistro.cliente?.telefone}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Informações Adicionais */}
                  {sinistro.informacoes && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-gray-500 uppercase text-xs mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Informações</div>
                      <div className="text-sm text-gray-700 bg-gray-50 rounded p-2 whitespace-pre-line">{sinistro.informacoes}</div>
                    </div>
                  )}

                  {/* Status Flags no rodapé */}
                  {(sinistro.oficina || sinistro.abertura || sinistro.documentos || sinistro.vistoria_sinistro || sinistro.analise || sinistro.aprovacao || sinistro.pagamento || sinistro.concluido) && (
                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-100">
                      {sinistro.oficina && <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">Oficina</span>}
                      {sinistro.abertura && <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">Abertura</span>}
                      {sinistro.documentos && <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full">Documentos</span>}
                      {sinistro.vistoria_sinistro && <span className="bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full">Vistoria</span>}
                      {sinistro.analise && <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-0.5 rounded-full">Análise</span>}
                      {sinistro.aprovacao && <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">Aprovação</span>}
                      {sinistro.pagamento && <span className="bg-teal-100 text-teal-800 text-xs px-2 py-0.5 rounded-full">Pagamento</span>}
                      {sinistro.concluido && <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-full">Concluído</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && sinistros.length === 0 && (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-sm text-gray-500">Nenhum sinistro encontrado</div>
        </div>
      )}

      {/* Paginação */}
      {!loading && sinistros.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 px-4 py-3 bg-white rounded-lg shadow">
          <span className="text-xs" style={{ color: '#5d54a4' }}>
            Página {currentPage} de {totalPages}
          </span>
          
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modal de Sinistro */}
      <SinistroModal
        isOpen={showSinistroModal}
        onClose={handleCloseModal}
        sinistro={selectedSinistro}
        onSave={handleSaveSinistro}
        readOnly={modalReadOnly}
      />

      {/* Dialog de confirmação de exclusão */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Excluir Sinistro"
        message="Tem certeza que deseja excluir este sinistro?"
        details={sinistroToDelete ? [
          `Número: ${sinistroToDelete.numero_sinistro || '-'}`,
          `Data: ${sinistroToDelete.data ? new Date(sinistroToDelete.data).toLocaleDateString('pt-BR') : '-'}`,
          `Tipo: ${sinistroToDelete.tipo || '-'}`
        ] : []}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSinistroToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="#DC2626"
      />
    </div>
  );
};